<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import BlogSidebar from '@/components/publicSite/BlogSidebar.vue'
import PublicSiteFooter from '@/components/publicSite/PublicSiteFooter.vue'
import PublicSiteHeader from '@/components/publicSite/PublicSiteHeader.vue'
import { $api } from '@/utils/api'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

interface BlogPostCard {
  id: number
  title: string
  slug: string
  excerpt: string | null
  cover_image_url: string | null
  cover_image_alt: string | null
  author_name: string | null
  published_at: string | null
}

const route = useRoute()
const router = useRouter()

const posts = ref<BlogPostCard[]>([])
const isLoading = ref(true)
const page = ref(1)
const lastPage = ref(1)

const activeCategory = computed(() => (route.query.category as string) || '')
const activeTag = computed(() => (route.query.tag as string) || '')
const hasActiveFilter = computed(() => !!activeCategory.value || !!activeTag.value)

const fetchPosts = async () => {
  isLoading.value = true
  try {
    const params = new URLSearchParams({ page: String(page.value) })
    if (activeCategory.value)
      params.set('category', activeCategory.value)
    if (activeTag.value)
      params.set('tag', activeTag.value)

    const data = await $api<any>(`/blog?${params.toString()}`)
    posts.value = data.posts
    lastPage.value = data.last_page
  } finally {
    isLoading.value = false
  }
}

const goToPage = (p: number) => {
  page.value = p
  fetchPosts()
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const clearFilter = () => {
  router.push('/blog')
}

const formatDate = (date: string | null) => {
  if (!date)
    return ''

  return new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
}

watch(() => [route.query.category, route.query.tag], () => {
  page.value = 1
  fetchPosts()
})

onMounted(fetchPosts)
</script>

<template>
  <div class="blog-premium">
    <PublicSiteHeader />

    <header class="blog-hero">
      <div class="container text-center">
        <h1 class="text-h1 hero-title mb-4">Blog Appforge</h1>
        <p class="text-h6 hero-description mx-auto">Novità di prodotto, guide pratiche e approfondimenti per chi gestisce la propria attività con AppForge.</p>
      </div>
    </header>

    <section class="posts-section py-16">
      <div class="container">
        <VRow>
          <VCol cols="12" md="8">
            <div v-if="hasActiveFilter" class="active-filter-bar mb-6">
              <span>Filtro attivo: <strong>{{ activeCategory || activeTag }}</strong></span>
              <button type="button" class="clear-filter-btn" @click="clearFilter">
                <VIcon icon="tabler-x" size="16" /> Rimuovi
              </button>
            </div>

            <div v-if="isLoading" class="d-flex justify-center py-16">
              <VProgressCircular indeterminate color="primary" size="48" />
            </div>

            <div v-else-if="posts.length === 0" class="text-center py-16">
              <p class="text-h6" style="color:#E5EEF9;">Nessun articolo trovato.</p>
            </div>

            <div v-else class="posts-grid">
              <RouterLink v-for="post in posts" :key="post.id" :to="`/blog/${post.slug}`" class="post-card">
                <div class="post-card-image" :style="post.cover_image_url ? `background-image:url('${post.cover_image_url}')` : ''">
                  <VIcon v-if="!post.cover_image_url" icon="tabler-article" size="40" color="white" />
                </div>
                <div class="post-card-body">
                  <span class="post-card-date">{{ formatDate(post.published_at) }}</span>
                  <h2 class="post-card-title">{{ post.title }}</h2>
                  <p v-if="post.excerpt" class="post-card-excerpt">{{ post.excerpt }}</p>
                  <span class="post-card-cta">Leggi l'articolo &rarr;</span>
                </div>
              </RouterLink>
            </div>

            <div v-if="lastPage > 1" class="d-flex justify-center gap-2 mt-12">
              <VBtn
                v-for="p in lastPage"
                :key="p"
                :variant="p === page ? 'flat' : 'outlined'"
                :color="p === page ? 'primary' : 'white'"
                size="small"
                @click="goToPage(p)"
              >
                {{ p }}
              </VBtn>
            </div>
          </VCol>

          <VCol cols="12" md="4">
            <BlogSidebar />
          </VCol>
        </VRow>
      </div>
    </section>

    <PublicSiteFooter />
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;800&display=swap');

.blog-premium {
  font-family: 'Outfit', sans-serif;
  background-color: #0F172A;
  color: #F1F5F9;
  min-height: 100vh;
}

.container { max-width: 1600px; margin: 0 auto; padding: 0 40px; }

.blog-hero { padding: 100px 0 60px; }
.hero-title { color: #FFFFFF; }
.hero-description { color: #E5EEF9; max-width: 640px; }

.active-filter-bar {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  background: rgba(99, 102, 241, 0.1); border: 1px solid rgba(99, 102, 241, 0.25);
  border-radius: 12px; padding: 12px 20px; color: #E5EEF9; font-size: 14px;
}
.clear-filter-btn {
  display: flex; align-items: center; gap: 4px; background: none; border: none; color: #A5B4FC;
  cursor: pointer; font-size: 13px; font-weight: 600;
}

.posts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 32px;
}
.post-card {
  display: flex; flex-direction: column; text-decoration: none; color: inherit;
  background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px; overflow: hidden; transition: all 0.25s ease;
}
.post-card:hover { border-color: #6366F1; transform: translateY(-4px); }
.post-card-image {
  height: 180px; background-color: rgba(99, 102, 241, 0.15); background-size: cover; background-position: center;
  display: flex; align-items: center; justify-content: center;
}
.post-card-body { padding: 24px; display: flex; flex-direction: column; gap: 8px; flex: 1; }
.post-card-date { font-size: 13px; color: #A5B4FC; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
.post-card-title { font-size: 20px; font-weight: 700; color: #FFFFFF; margin: 0; }
.post-card-excerpt { color: #E5EEF9; font-size: 14.5px; line-height: 1.6; flex: 1; }
.post-card-cta { color: #A5B4FC; font-weight: 600; font-size: 14px; margin-top: 8px; }

@media (max-width: 600px) {
  .container { padding: 0 20px; }
}
</style>
